import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  FileTextOutlined,
  HistoryOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { reportAPI, drugAPI } from '../services/api';
import { useResponsive } from '../contexts/ResponsiveContext';
import MobileSectionCard from './mobile/MobileSectionCard';
import PKCurve from './PKCurve';
import { tdmDataToPoints } from '../utils/pkCurveGen';
import ClinicalMarkdown from './ClinicalMarkdown';
import { formatDoseMgKg } from '../utils/doseDisplayFormat';
import {
  convertCanonicalValue,
  convertUnitValue,
  formatCanonicalDose,
  projectCanonicalConcentrationCurve,
  projectCanonicalTdmPoints,
  unitFromResult,
} from '../utils/quantityUnits';
import {
  getConcentrationDisplayUnit,
  getDefaultAdministrationMode,
  normalizeAdministrationMode,
} from '../utils/doseMethodVisibility';
import type { CalculationRecord } from '../types';

const { Text } = Typography;

interface CalculationHistoryProps {
  caseId: string;
  drugName?: string;
  drugId?: string;
  onLatestCalculationLoaded?: (record: CalculationRecord | null) => void;
}

const calcTypeConfig: Record<string, { label: string; color: string }> = {
  initial: { label: '初始给药', color: 'blue' },
  optimize: { label: 'TDM 优化', color: 'green' },
  pk_formula: { label: 'PK 公式法', color: 'orange' },
  remedial: { label: '补服/漏服', color: 'purple' },
};

const formatTime = (value?: string) => (value ? new Date(value).toLocaleString('zh-CN') : '-');

const CalculationHistory: React.FC<CalculationHistoryProps> = ({ caseId, drugName, drugId, onLatestCalculationLoaded }) => {
  const { isMobileShell } = useResponsive();
  const [records, setRecords] = useState<CalculationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [drugConfig, setDrugConfig] = useState<any>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        const data = await reportAPI.history(caseId);
        const calculations = data.calculations || [];
        setRecords(calculations);
        const latestByType = new Map<string, string>();
        calculations.forEach((record) => {
          if (!latestByType.has(record.calculation_type)) latestByType.set(record.calculation_type, record.id);
        });
        setExpandedIds(Array.from(latestByType.values()));
        onLatestCalculationLoaded?.(
          calculations.find((record) => record.calculation_type === 'optimize') || calculations[0] || null,
        );
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [caseId, onLatestCalculationLoaded]);

  const renderOriginalInput = (record: CalculationRecord) => {
    const patientData = record.patient_data || {};
    const results = record.results as Record<string, any>;
    const doseMassUnit = unitFromResult(results, 'dose', drugConfig?.units?.dose?.display_mass_unit || 'mg');
    const concentrationUnit = getRecordConcentrationUnit(record);
    const formatInputDose = (value: unknown) => {
      const numeric = Number(value);
      return Number.isFinite(numeric)
        ? formatCanonicalDose(numeric, { dose_mass_unit: doseMassUnit })
        : '-';
    };
    const formatInputConcentration = (value: unknown) => {
      const converted = convertCanonicalValue(value, concentrationUnit, 'concentration');
      return converted == null ? '-' : `${converted} ${concentrationUnit}`;
    };
    const commonItems: Array<[string, React.ReactNode]> = [
      ['模型', record.model_id || results.model_id || '-'],
      ['计算方法', results.primary_method || results.selected_method || results.target_type || '-'],
      ['给药模式', results.administration_mode || '-'],
    ];
    let specificItems: Array<[string, React.ReactNode]> = [];

    if (record.calculation_type === 'remedial') {
      const input = patientData.remedial_input || {};
      const regimen = input.current_regimen || results.current_regimen || {};
      specificItems = [
        ['当前单次剂量', formatInputDose(regimen.dose_mg)],
        ['给药间隔', regimen.interval_h != null ? `${regimen.interval_h} h` : '-'],
        ['计划服药时间', input.scheduled_time ? formatTime(input.scheduled_time) : '-'],
        ['发现/拟补服时间', input.noticed_time ? formatTime(input.noticed_time) : '-'],
        ['风险侧重', input.risk_preference || results.risk_preference || 'balanced'],
        ['优先整规格', input.prefer_simple_doses ? '是' : '否'],
      ];
    } else if (record.calculation_type === 'pk_formula') {
      const input = patientData.pk_formula_input || {};
      specificItems = [
        ['原给药剂量', formatInputDose(input.dose)],
        ['实测峰浓度', formatInputConcentration(input.measured_peak)],
        ['实测谷浓度', formatInputConcentration(input.measured_trough)],
        ['峰采样时间', input.t_peak != null ? `${input.t_peak} h` : '-'],
        ['谷采样至下次给药', input.t_trough != null ? `${input.t_trough} h` : '-'],
        ['给药间隔', input.dosing_interval != null ? `${input.dosing_interval} h` : '-'],
      ];
    } else {
      const input = record.calculation_type === 'optimize'
        ? (patientData.optimize_input || {})
        : (patientData.initial_input || {});
      specificItems = [
        ['目标类型', input.target_type || results.primary_method || results.selected_method || '-'],
        ['目标值', input.target_value ?? input.target_auc ?? results.target_value ?? '-'],
        ['MIC', input.mic ?? results.mic ?? '-'],
      ];
      if (record.calculation_type === 'optimize') {
        const regimens = input.dosing_regimens || results.regimen_history || [];
        const tdm = input.tdm_data || record.tdm_data || [];
        specificItems.push(
          ['原给药历史', regimens.length ? regimens.map((item: any) => `${formatInputDose(item.dose ?? item.dose_mg)} / q${item.dosing_interval ?? item.interval_h ?? '-'}h`).join('；') : '-'],
          ['原 TDM 记录', tdm.length ? tdm.map((item: any) => `${formatInputConcentration(item.concentration)}（给药后 ${item.time_after_dose ?? item.absolute_time_h ?? '-'} h）`).join('；') : '-'],
        );
      }
    }

    return (
      <>
        <Divider orientation="left" plain>原始录入数据</Divider>
        <Descriptions bordered size="small" column={isMobileShell ? 1 : { xs: 1, sm: 2, md: 3 }} style={{ marginBottom: 16 }}>
          {[...commonItems, ...specificItems].map(([label, value]) => (
            <Descriptions.Item key={label} label={label}>{value}</Descriptions.Item>
          ))}
        </Descriptions>
      </>
    );
  };

  useEffect(() => {
    const lookupKey = drugId || drugName;
    if (!lookupKey) return;
    drugAPI.get(lookupKey).then(setDrugConfig).catch(() => {});
  }, [drugId, drugName]);

  const primaryMethod = drugConfig?.calculation_methods?.[0] || 'Target AUC';

  const getRecordPrimaryMethod = (record: CalculationRecord) => {
    const results = record.results as Record<string, any>;
    return String(results.primary_method || results.selected_method || primaryMethod);
  };

  const getRecordAdministrationMode = (record: CalculationRecord) => {
    const results = record.results as Record<string, any>;
    return normalizeAdministrationMode(
      results.administration_mode || drugConfig?.default_administration_mode,
      getDefaultAdministrationMode(drugConfig),
    );
  };

  const getRecordConcentrationUnit = (record: CalculationRecord, method = getRecordPrimaryMethod(record)) => {
    const results = record.results as Record<string, any>;
    const pkpdResults = results.pkpd_results || {};
    return String(
      unitFromResult(results, 'concentration', '')
      || pkpdResults[method]?.unit
      || pkpdResults['Target Concentration']?.unit
      || pkpdResults['Target Cmax']?.unit
      || getConcentrationDisplayUnit(drugConfig, method, getRecordAdministrationMode(record)),
    );
  };

  const handleViewReport = async (record: CalculationRecord) => {
    try {
      const html = record.report_id
        ? await reportAPI.view(record.report_id)
        : await reportAPI.generate(record.id);
      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '报告生成失败');
    }
  };

  const getRecommendedDose = (record: CalculationRecord) => {
    const results = record.results as Record<string, any>;
    if (results.recommended_dose_mg == null) return '-';
    const massUnit = unitFromResult(results, 'dose', 'mg');
    const perKgValue = results.recommended_dose_mg_kg != null
      ? convertUnitValue(results.recommended_dose_mg_kg, 'mg/kg', `${massUnit}/kg`, 'dose_per_weight')
      : null;
    const perKg = perKgValue != null ? ` (${formatDoseMgKg(perKgValue)} ${massUnit}/kg)` : '';
    return `${formatCanonicalDose(results.recommended_dose_mg, { dose_mass_unit: massUnit })}${perKg}`;
  };

  const getTargetMetric = (record: CalculationRecord) => {
    const results = record.results as Record<string, any>;
    const method = getRecordPrimaryMethod(record);

    if (method === '%fT>MIC') {
      const pkpd = results.pkpd_results?.['%fT>MIC'];
      if (pkpd?.value != null) {
        const pta = pkpd.pta != null ? ` · PTA ${pkpd.pta}%` : '';
        return `${typeof pkpd.value === 'number' ? pkpd.value.toFixed(1) : pkpd.value}%${pta}`;
      }
      return '-';
    }

    if (method === 'Target Concentration' || method === 'Target Cmax') {
      const unit = getRecordConcentrationUnit(record, method);
      const primaryValue = method === 'Target Cmax'
        ? (results.predicted_cmax ?? results.true_peak)
        : (results.predicted_cmin ?? results.true_trough);
      if (primaryValue != null) return `${convertCanonicalValue(primaryValue, unit, 'concentration')} ${unit}`;
      return '-';
    }

    if (method === 'Cmax/MIC') {
      const cmaxMic = results.cmax_mic ?? results.pkpd_results?.['Cmax/MIC']?.value;
      return cmaxMic != null ? `${cmaxMic}` : '-';
    }

    if (method === 'AUC/MIC') {
      const aucMic = results.auc24_mic ?? results.pkpd_results?.['AUC/MIC']?.value;
      if (aucMic != null) return `${aucMic}`;
    }
    const aucUnit = unitFromResult(results, 'auc', 'mg*h/L');
    if (results.predicted_auc24 != null) return `${convertCanonicalValue(results.predicted_auc24, aucUnit, 'auc')} ${aucUnit}`;
    if (results.auc24 != null) return `${convertCanonicalValue(results.auc24, aucUnit, 'auc')} ${aucUnit}`;
    return '-';
  };

  const targetMetricTitle = useMemo(() => {
    if (primaryMethod === '%fT>MIC') return '%fT>MIC / PTA';
    if (primaryMethod === 'Target Concentration') return '预测 Cmin';
    if (primaryMethod === 'Target Cmax') return '预测 Cmax';
    if (primaryMethod === 'Cmax/MIC') return '预测 Cmax/MIC';
    if (primaryMethod === 'AUC/MIC') return '预测 AUC24/MIC';
    return '预测 AUC24';
  }, [primaryMethod]);

  const renderDetails = (record: CalculationRecord) => {
    const results = record.results as Record<string, any>;
    const safety = results.safety_check || { safe: true, warnings: [], errors: [] };
    const concentrationUnit = getRecordConcentrationUnit(record);
    const aucUnit = unitFromResult(results, 'auc', 'mg*h/L');
    const isOralRecord = getRecordAdministrationMode(record) === 'oral';
    const isMeropenemFormula = results.strategy_key === 'meropenem_chen_2018_ss_two_point_ftmic';
    const formulaCurveContext = isMeropenemFormula ? results.pk_formula_curve_context : undefined;
    const simulationData = projectCanonicalConcentrationCurve(
      formulaCurveContext?.mode === 'steady_state_two_intervals' && formulaCurveContext?.curve?.time?.length
        ? formulaCurveContext.curve
        : results.simulation_data,
      concentrationUnit,
    );
    const combinedCurveData = projectCanonicalConcentrationCurve(results.combined_curve, concentrationUnit);
    const formulaTdmPoints = formulaCurveContext?.mode === 'steady_state_two_intervals'
      && Array.isArray(formulaCurveContext.sample_points)
      ? formulaCurveContext.sample_points.map((point: any) => ({
        time: Number(point.time_h),
        concentration: Number(point.concentration),
        label: point.key === 'early' ? '\u8f83\u65e9\u6837\u672c' : '\u8f83\u665a\u6837\u672c',
        before_next_dose_h: Number(point.before_next_dose_h),
      }))
      : undefined;
    const tdmPoints = formulaTdmPoints?.length
      ? projectCanonicalTdmPoints(formulaTdmPoints, concentrationUnit) || []
      : Array.isArray(results.tdm_points) && results.tdm_points.length > 0
        ? projectCanonicalTdmPoints(results.tdm_points, concentrationUnit) || []
        : tdmDataToPoints(record.tdm_data, record.patient_data, record.calculation_type, results.regimen_history);
    const displayConcentration = (value: unknown) => convertCanonicalValue(value, concentrationUnit, 'concentration');
    const displayAuc = (value: unknown) => convertCanonicalValue(value, aucUnit, 'auc');

    return (
      <div style={{ paddingTop: 4 }}>
        {renderOriginalInput(record)}
        <Descriptions
          bordered
          size="small"
          column={isMobileShell ? 1 : { xs: 1, sm: 2, md: 3 }}
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="方案剂量">{getRecommendedDose(record)}</Descriptions.Item>
          {results.dosing_interval_h != null ? (
            <Descriptions.Item label="给药间隔">每 {results.dosing_interval_h} 小时</Descriptions.Item>
          ) : null}
          {results.infusion_duration_h != null && !isOralRecord ? (
            <Descriptions.Item label="输注时长">{results.infusion_duration_h} 小时</Descriptions.Item>
          ) : null}
          {results.predicted_auc24 != null ? (
            <Descriptions.Item label="预测 AUC24">{displayAuc(results.predicted_auc24)} {aucUnit}</Descriptions.Item>
          ) : null}
          {results.auc24 != null ? (
            <Descriptions.Item label="预测 AUC24">{displayAuc(results.auc24)} {aucUnit}</Descriptions.Item>
          ) : null}
          {results.auc24_mic != null ? (
            <Descriptions.Item label="预测 AUC24/MIC">{results.auc24_mic}</Descriptions.Item>
          ) : null}
          {results.predicted_cmin != null ? (
            <Descriptions.Item label="预测 Cmin">{displayConcentration(results.predicted_cmin)} {concentrationUnit}</Descriptions.Item>
          ) : null}
          {results.predicted_cmax != null ? (
            <Descriptions.Item label="预测 Cmax">{displayConcentration(results.predicted_cmax)} {concentrationUnit}</Descriptions.Item>
          ) : null}
          {results.true_peak != null ? (
            <Descriptions.Item label="预测峰浓度">{displayConcentration(results.true_peak)} {concentrationUnit}</Descriptions.Item>
          ) : null}
          {results.true_trough != null ? (
            <Descriptions.Item label="预测谷浓度">{displayConcentration(results.true_trough)} {concentrationUnit}</Descriptions.Item>
          ) : null}
          {results.ke != null ? (
            <Descriptions.Item label="消除速率常数">{results.ke} h⁻¹</Descriptions.Item>
          ) : null}
          {results.half_life != null ? (
            <Descriptions.Item label="半衰期">{results.half_life} h</Descriptions.Item>
          ) : null}
          {results.vd != null ? (
            <Descriptions.Item label="分布容积">
              {results.vd} L{results.vd_per_kg != null ? ` (${results.vd_per_kg} L/kg)` : ''}
            </Descriptions.Item>
          ) : null}
          {results.clearance != null ? (
            <Descriptions.Item label="清除率">{results.clearance} L/h</Descriptions.Item>
          ) : null}
        </Descriptions>

        {results.pk_parameters && Object.keys(results.pk_parameters).length > 0 ? (
          <>
            <Divider orientation="left" plain>PK 参数</Divider>
            <Descriptions
              bordered
              size="small"
              column={isMobileShell ? 1 : 3}
              style={{ marginBottom: 16 }}
            >
              {Object.entries(results.pk_parameters).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  {typeof value === 'number' ? value.toFixed(4) : String(value)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        ) : null}

        <div style={{ marginBottom: 16 }}>
          <Space style={{ marginBottom: 8 }}>
            <SafetyOutlined />
            <Text strong>安全检查</Text>
            <Tag color={safety.safe ? 'green' : 'red'}>{safety.safe ? '通过' : '存在风险'}</Tag>
          </Space>
          {safety.warnings?.map((warning: any, index: number) => (
            <Alert
              key={`warning-${index}`}
              type="warning"
              banner
              message={typeof warning === 'string' ? warning : (warning?.msg || JSON.stringify(warning))}
              style={{ marginTop: 4 }}
            />
          ))}
          {safety.errors?.map((error: any, index: number) => (
            <Alert
              key={`error-${index}`}
              type="error"
              banner
              message={typeof error === 'string' ? error : (error?.msg || JSON.stringify(error))}
              style={{ marginTop: 4 }}
            />
          ))}
        </div>

        {((simulationData?.time && simulationData?.concentration) || combinedCurveData) ? (
          <>
            <Divider orientation="left" plain>药时曲线</Divider>
            <PKCurve
              simulationData={simulationData}
              combinedCurveData={combinedCurveData}
              tdmPoints={tdmPoints.length ? tdmPoints : undefined}
              formulaCurveContext={formulaCurveContext}
              autoScale={isMeropenemFormula}
              concentrationUnit={concentrationUnit}
            />
          </>
        ) : null}

        {results.clinical_interpretation ? (
          <>
            <Divider orientation="left" plain>临床解读</Divider>
            <Card size="small" style={{ background: '#fafafa' }}>
              <ClinicalMarkdown>{results.clinical_interpretation}</ClinicalMarkdown>
            </Card>
          </>
        ) : null}
      </div>
    );
  };

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 60,
      render: (_: unknown, __: CalculationRecord, index: number) => index + 1,
    },
    {
      title: '计算类型',
      dataIndex: 'calculation_type',
      key: 'calculation_type',
      width: 120,
      render: (value: string) => {
        const config = calcTypeConfig[value] || { label: value, color: 'default' };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: '使用模型',
      dataIndex: 'model_id',
      key: 'model_id',
      width: 180,
      render: (value: string | undefined) => value || <Text type="secondary">-</Text>,
    },
    {
      title: '方案剂量',
      key: 'dose',
      width: 180,
      render: (_: unknown, record: CalculationRecord) => getRecommendedDose(record),
    },
    {
      title: targetMetricTitle,
      key: 'target_metric',
      width: 180,
      render: (_: unknown, record: CalculationRecord) => getTargetMetric(record),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: formatTime,
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: unknown, record: CalculationRecord) => (
        <Button
          type="link"
          size="small"
          icon={<FileTextOutlined />}
          onClick={() => handleViewReport(record)}
        >
          {record.report_id ? '查看报告' : '生成报告'}
        </Button>
      ),
    },
  ];

  const toggleExpanded = (recordId: string) => {
    setExpandedIds((prev) => (
      prev.includes(recordId)
        ? prev.filter((item) => item !== recordId)
        : [...prev, recordId]
    ));
  };

  const renderMobileCards = () => {
    if (!records.length) {
      return <Text type="secondary">暂无计算记录</Text>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {records.map((record, index) => {
          const config = calcTypeConfig[record.calculation_type] || { label: record.calculation_type, color: 'default' };
          const expanded = expandedIds.includes(record.id);
          return (
            <MobileSectionCard
              key={record.id}
              title={`第 ${index + 1} 次计算`}
              extra={<Tag color={config.color}>{config.label}</Tag>}
            >
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>方案剂量</Text>
                <Text strong>{getRecommendedDose(record)}</Text>
              </div>
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>{targetMetricTitle}</Text>
                <Text>{getTargetMetric(record)}</Text>
              </div>
              <div style={{ marginBottom: 14 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>计算时间</Text>
                <Text>{formatTime(record.created_at)}</Text>
              </div>
              <div style={{ display: 'flex', gap: 10, marginBottom: expanded ? 12 : 0 }}>
                <Button type="primary" icon={<FileTextOutlined />} block onClick={() => handleViewReport(record)}>
                  {record.report_id ? '查看报告' : '生成报告'}
                </Button>
                <Button block onClick={() => toggleExpanded(record.id)}>
                  {expanded ? '收起详情' : '展开详情'}
                </Button>
              </div>
              {expanded ? renderDetails(record) : null}
            </MobileSectionCard>
          );
        })}
      </div>
    );
  };

  return (
    <Card
      title={(
        <Space>
          <HistoryOutlined />
          <span>计算历史</span>
        </Space>
      )}
      loading={loading}
    >
      {isMobileShell ? (
        renderMobileCards()
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={records}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '暂无计算记录' }}
          scroll={{ x: 960 }}
          expandable={{
            expandedRowRender: renderDetails,
            rowExpandable: (record) => Boolean(record.results),
          }}
        />
      )}
    </Card>
  );
};

export default CalculationHistory;
